import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Cropper from 'react-easy-crop';
import { addArticle, updateArticle, deleteArticle, generateUniqueSlug, isSlugAvailable } from '../services/articleService';
import { uploadArticleImage } from '../services/storageService';
import CoursePicker from './CoursePicker';

// Helper to create cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
}

const CATEGORIES = [
  { value: 'architects', label: 'Featured Architects' },
  { value: 'travel', label: 'Travel Guides' },
  { value: 'media', label: 'Golf Media' }
];

const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link', 'image'],
    ['clean']
  ]
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'link', 'image'
];

function AdminArticleModal({ isOpen, onClose, editingArticle, golfCourses, onArticleUpdated }) {
  // Basic info state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [category, setCategory] = useState('travel');
  const [image, setImage] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [readTime, setReadTime] = useState('5 min read');

  // Content state
  const [content, setContent] = useState('');

  // Map config state
  const [hasMap, setHasMap] = useState(false);
  const [mapTitle, setMapTitle] = useState('');
  const [mapDescription, setMapDescription] = useState('');
  const [courseSelectionMode, setCourseSelectionMode] = useState('architect'); // 'architect' or 'specific'
  const [selectedArchitect, setSelectedArchitect] = useState('');
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);

  // Publish state
  const [published, setPublished] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [slugError, setSlugError] = useState('');
  const [architectSearch, setArchitectSearch] = useState('');
  const [showArchitectDropdown, setShowArchitectDropdown] = useState(false);

  // Image upload state
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);

  // Preview image upload and crop state
  const [previewImageUploading, setPreviewImageUploading] = useState(false);
  const previewFileInputRef = useRef(null);
  const [showCropper, setShowCropper] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Get unique architect names from courses
  const architectOptions = useMemo(() => {
    const architects = new Set();
    golfCourses.forEach(course => {
      if (course.course_architect) {
        architects.add(course.course_architect);
      }
    });
    return Array.from(architects).sort();
  }, [golfCourses]);

  // Filter architects based on search
  const filteredArchitects = useMemo(() => {
    if (!architectSearch) return architectOptions.slice(0, 50);
    const search = architectSearch.toLowerCase();
    return architectOptions.filter(a => a.toLowerCase().includes(search)).slice(0, 50);
  }, [architectOptions, architectSearch]);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setImageUploading(true);
    setError('');

    try {
      const imageUrl = await uploadArticleImage(file, slug || 'general');

      // Create styled image HTML
      const imageHtml = `<img src="${imageUrl}" alt="Uploaded image" style="max-width:600px;width:100%;height:auto;display:block;margin:1em auto;border-radius:8px;" />`;

      // Insert image into Quill editor at cursor position
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection(true);
        quill.clipboard.dangerouslyPasteHTML(range.index, imageHtml);
        quill.setSelection(range.index + 1);
      } else {
        // Fallback: append image HTML to content
        setContent(prev => prev + imageHtml);
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setImageUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle preview image file selection - opens cropper
  const handlePreviewImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setShowCropper(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);

    // Reset file input
    if (previewFileInputRef.current) {
      previewFileInputRef.current.value = '';
    }
  };

  // Handle crop complete
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Upload cropped preview image
  const handleCropConfirm = async () => {
    if (!cropImageSrc || !croppedAreaPixels) return;

    setPreviewImageUploading(true);
    setError('');

    try {
      const croppedBlob = await getCroppedImg(cropImageSrc, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], 'preview.jpg', { type: 'image/jpeg' });
      const imageUrl = await uploadArticleImage(croppedFile, `${slug || 'general'}/preview`);
      setImage(imageUrl);
      setShowCropper(false);
      setCropImageSrc(null);
    } catch (err) {
      console.error('Error uploading preview image:', err);
      setError('Failed to upload preview image. Please try again.');
    } finally {
      setPreviewImageUploading(false);
    }
  };

  // Cancel cropping
  const handleCropCancel = () => {
    setShowCropper(false);
    setCropImageSrc(null);
  };

  // Reset form when modal opens/closes or editingArticle changes
  useEffect(() => {
    if (isOpen) {
      if (editingArticle) {
        // Populate form with existing article data
        setTitle(editingArticle.title || '');
        setSlug(editingArticle.slug || '');
        setSlugManuallyEdited(true); // Don't auto-generate for edits
        setCategory(editingArticle.category || 'travel');
        setImage(editingArticle.image || '');
        setExcerpt(editingArticle.excerpt || '');
        setReadTime(editingArticle.readTime || '5 min read');
        setContent(editingArticle.content || '');
        setHasMap(editingArticle.hasMap || false);
        setMapTitle(editingArticle.mapConfig?.title || '');
        setMapDescription(editingArticle.mapConfig?.description || '');
        setPublished(editingArticle.published || false);

        // Determine course selection mode
        if (editingArticle.mapConfig?.architect) {
          setCourseSelectionMode('architect');
          setSelectedArchitect(editingArticle.mapConfig.architect);
          setSelectedCourseIds([]);
        } else if (editingArticle.mapConfig?.selectedCourseIds?.length > 0) {
          setCourseSelectionMode('specific');
          setSelectedArchitect('');
          setSelectedCourseIds(editingArticle.mapConfig.selectedCourseIds);
        } else {
          setCourseSelectionMode('architect');
          setSelectedArchitect('');
          setSelectedCourseIds([]);
        }
      } else {
        // Reset form for new article
        setTitle('');
        setSlug('');
        setSlugManuallyEdited(false);
        setCategory('travel');
        setImage('');
        setExcerpt('');
        setReadTime('5 min read');
        setContent('');
        setHasMap(false);
        setMapTitle('');
        setMapDescription('');
        setCourseSelectionMode('architect');
        setSelectedArchitect('');
        setSelectedCourseIds([]);
        setPublished(false);
      }
      setError('');
      setSlugError('');
      // Reset cropper state
      setShowCropper(false);
      setCropImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  }, [isOpen, editingArticle]);

  // Auto-generate slug from title (unless manually edited)
  useEffect(() => {
    if (!slugManuallyEdited && title) {
      const autoSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setSlug(autoSlug);
    }
  }, [title, slugManuallyEdited]);

  // Validate slug availability
  useEffect(() => {
    const validateSlug = async () => {
      if (!slug) {
        setSlugError('');
        return;
      }
      const available = await isSlugAvailable(slug, editingArticle?.id);
      if (!available) {
        setSlugError('This slug is already in use');
      } else {
        setSlugError('');
      }
    };
    const timeoutId = setTimeout(validateSlug, 300);
    return () => clearTimeout(timeoutId);
  }, [slug, editingArticle]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!title || !slug) {
      setError('Title and slug are required.');
      setLoading(false);
      return;
    }

    if (slugError) {
      setError('Please fix the slug error before saving.');
      setLoading(false);
      return;
    }

    try {
      const articleData = {
        title,
        slug,
        category,
        image,
        excerpt,
        content,
        date: editingArticle?.date || new Date(),
        readTime,
        hasMap,
        mapConfig: hasMap ? {
          title: mapTitle,
          description: mapDescription,
          architect: courseSelectionMode === 'architect' ? selectedArchitect : null,
          selectedCourseIds: courseSelectionMode === 'specific' ? selectedCourseIds : []
        } : null,
        published
      };

      if (editingArticle) {
        await updateArticle(editingArticle.id, articleData);
      } else {
        await addArticle(articleData);
      }

      if (onArticleUpdated) {
        onArticleUpdated();
      }
      onClose();
    } catch (err) {
      console.error('Error saving article:', err);
      setError('Failed to save article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this article? This cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      await deleteArticle(editingArticle.id);
      if (onArticleUpdated) {
        onArticleUpdated();
      }
      onClose();
    } catch (err) {
      console.error('Error deleting article:', err);
      setError('Failed to delete article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" style={{
      zIndex: 3000,
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.3)',
      overflowY: 'auto'
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content" style={{
        maxWidth: 800,
        width: '95%',
        maxHeight: '95vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
        margin: '20px auto',
        padding: '24px'
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, color: '#1D665D' }}>
            {editingArticle ? 'Edit Article' : 'Create New Article'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#666'
            }}
          >
            x
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info Section */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: 16, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
              Basic Info
            </h3>

            {/* Title */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Article title"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 14,
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Slug */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                URL Slug *
              </label>
              <input
                type="text"
                value={slug}
                onChange={e => {
                  setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                  setSlugManuallyEdited(true);
                }}
                placeholder="url-slug"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: slugError ? '1px solid #dc2626' : '1px solid #ddd',
                  fontSize: 14,
                  boxSizing: 'border-box'
                }}
              />
              {slugError && (
                <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{slugError}</div>
              )}
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                URL: /article/{slug || 'your-slug'}
              </div>
            </div>

            {/* Category */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                Category
              </label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 14,
                  boxSizing: 'border-box',
                  background: '#fff'
                }}
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Preview Image */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                Preview Image
              </label>

              {/* Upload button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <input
                  type="file"
                  ref={previewFileInputRef}
                  onChange={handlePreviewImageSelect}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => previewFileInputRef.current?.click()}
                  disabled={previewImageUploading}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: '1px solid #1D665D',
                    background: previewImageUploading ? '#e5e7eb' : '#fff',
                    color: '#1D665D',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: previewImageUploading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  {previewImageUploading ? 'Uploading...' : 'Upload & Crop Image'}
                </button>
                <span style={{ fontSize: 12, color: '#666' }}>
                  Select area to use as preview
                </span>
              </div>

              {/* Or enter URL manually */}
              <input
                type="text"
                value={image}
                onChange={e => setImage(e.target.value)}
                placeholder="Or paste image URL here..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 14,
                  boxSizing: 'border-box'
                }}
              />
              {image && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={image}
                    alt="Preview"
                    style={{ maxWidth: 200, maxHeight: 120, borderRadius: 8, objectFit: 'cover' }}
                    onError={e => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            {/* Excerpt */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                Excerpt
              </label>
              <textarea
                value={excerpt}
                onChange={e => setExcerpt(e.target.value)}
                placeholder="Brief description for article cards..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 14,
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Read Time */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                Read Time
              </label>
              <input
                type="text"
                value={readTime}
                onChange={e => setReadTime(e.target.value)}
                placeholder="5 min read"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 14,
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Content Section */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: 16, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
              Article Content
            </h3>

            {/* Image Upload Button */}
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={imageUploading}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: '1px solid #1D665D',
                  background: imageUploading ? '#e5e7eb' : '#fff',
                  color: '#1D665D',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: imageUploading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                {imageUploading ? (
                  <>Uploading...</>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21,15 16,10 5,21"/>
                    </svg>
                    Upload Image
                  </>
                )}
              </button>
              <span style={{ fontSize: 12, color: '#666' }}>
                Images are uploaded to Firebase Storage (max 5MB)
              </span>
            </div>

            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #ddd' }}>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={content}
                onChange={setContent}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                style={{ minHeight: 300 }}
              />
            </div>
          </div>

          {/* Map Configuration Section */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: 16, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
              Map Configuration
            </h3>

            {/* Has Map Toggle */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={hasMap}
                  onChange={e => setHasMap(e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ fontWeight: 500, color: '#333' }}>Include Interactive Map</span>
              </label>
            </div>

            {hasMap && (
              <>
                {/* Map Title */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                    Map Title
                  </label>
                  <input
                    type="text"
                    value={mapTitle}
                    onChange={e => setMapTitle(e.target.value)}
                    placeholder="e.g., Chicago Golf Courses"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      fontSize: 14,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Map Description */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                    Map Description
                  </label>
                  <input
                    type="text"
                    value={mapDescription}
                    onChange={e => setMapDescription(e.target.value)}
                    placeholder="Brief description of the map..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      fontSize: 14,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Course Selection Mode */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#333' }}>
                    Course Selection
                  </label>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="courseSelectionMode"
                        value="architect"
                        checked={courseSelectionMode === 'architect'}
                        onChange={() => setCourseSelectionMode('architect')}
                      />
                      <span>Filter by Architect</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="courseSelectionMode"
                        value="specific"
                        checked={courseSelectionMode === 'specific'}
                        onChange={() => setCourseSelectionMode('specific')}
                      />
                      <span>Select Specific Courses</span>
                    </label>
                  </div>
                </div>

                {/* Architect Selection */}
                {courseSelectionMode === 'architect' && (
                  <div style={{ marginBottom: 16, position: 'relative' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                      Architect Name
                    </label>
                    <input
                      type="text"
                      value={selectedArchitect || architectSearch}
                      onChange={e => {
                        setArchitectSearch(e.target.value);
                        setSelectedArchitect('');
                        setShowArchitectDropdown(true);
                      }}
                      onFocus={() => setShowArchitectDropdown(true)}
                      placeholder="Search for an architect..."
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '1px solid #ddd',
                        fontSize: 14,
                        boxSizing: 'border-box'
                      }}
                    />
                    {showArchitectDropdown && filteredArchitects.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        maxHeight: 200,
                        overflowY: 'auto',
                        zIndex: 100
                      }}>
                        {filteredArchitects.map(arch => (
                          <div
                            key={arch}
                            onClick={() => {
                              setSelectedArchitect(arch);
                              setArchitectSearch('');
                              setShowArchitectDropdown(false);
                            }}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee'
                            }}
                            onMouseEnter={e => e.target.style.background = '#f5f5f5'}
                            onMouseLeave={e => e.target.style.background = '#fff'}
                          >
                            {arch}
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedArchitect && (
                      <div style={{ marginTop: 8, color: '#1D665D', fontSize: 14 }}>
                        Selected: <strong>{selectedArchitect}</strong>
                        <button
                          type="button"
                          onClick={() => setSelectedArchitect('')}
                          style={{
                            marginLeft: 8,
                            background: 'none',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontSize: 12
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Specific Course Selection */}
                {courseSelectionMode === 'specific' && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                      Select Courses
                    </label>
                    <CoursePicker
                      golfCourses={golfCourses}
                      selectedCourseIds={selectedCourseIds}
                      onSelectionChange={setSelectedCourseIds}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Publish Section */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: 16, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
              Visibility
            </h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={published}
                onChange={e => setPublished(e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              <span style={{ fontWeight: 500, color: '#333' }}>
                Make public (visible to everyone)
              </span>
            </label>
            {!published && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#666', marginLeft: 26 }}>
                Currently a draft - only you can see it
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div style={{ color: '#dc2626', marginBottom: 16, fontSize: 14 }}>
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#1D665D',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Saving...' : editingArticle ? 'Update Article' : 'Create Article'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  background: '#fff',
                  color: '#666',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
            {editingArticle && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: '1px solid #dc2626',
                  background: '#fff',
                  color: '#dc2626',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Delete Article
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Cropper Modal */}
      {showCropper && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            zIndex: 4000,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Cropper area */}
          <div style={{ position: 'relative', flex: 1 }}>
            <Cropper
              image={cropImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={16 / 9}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          {/* Controls */}
          <div style={{
            background: '#fff',
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
              <span style={{ fontSize: 14, color: '#666' }}>Zoom:</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{ flex: 1, maxWidth: 200 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={handleCropCancel}
                style={{
                  padding: '10px 20px',
                  borderRadius: 6,
                  border: '1px solid #ddd',
                  background: '#fff',
                  color: '#666',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                disabled={previewImageUploading}
                style={{
                  padding: '10px 20px',
                  borderRadius: 6,
                  border: 'none',
                  background: previewImageUploading ? '#9ca3af' : '#1D665D',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: previewImageUploading ? 'not-allowed' : 'pointer'
                }}
              >
                {previewImageUploading ? 'Uploading...' : 'Crop & Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminArticleModal;
